import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Table, getPaginationLabels, ToggleCell } from '@components/Table';
import { Typography } from '@components/Typography';
import { EntityChip, ChipsCell } from '@components/EntityChips';
import ActionIconButton from '@components/ActionIconButton';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { menusActions } from '@actions';
import { menusService } from '@services';
import { handleApiError } from '@helpers/api';

const renderChipsCell = (menu, companyNamesById) => {
    const names = (menu.locationIds || []).map(id => companyNamesById.get(id)).filter(Boolean);
    if (!names.length) {
        return (
            <ChipsCell>
                <Typography variant="body2">--</Typography>
            </ChipsCell>
        );
    }
    return (
        <ChipsCell>
            {names.map((name, index) => (
                <EntityChip key={`${name}-${index}`} label={name} />
            ))}
        </ChipsCell>
    );
};

const countItems = menu =>
    (menu.document?.sections || []).reduce((total, section) => total + (section.items?.length || 0), 0);

const getTableHeader = t => [
    {
        key: 0,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.name')}
            </Typography>
        ),
    },
    {
        key: 1,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.locations')}
            </Typography>
        ),
    },
    {
        key: 2,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('MenusPage.sections')}
            </Typography>
        ),
    },
    {
        key: 3,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('MenusPage.items')}
            </Typography>
        ),
    },
    {
        key: 4,
        align: 'center',
        component: (
            <Typography align="center" variant="subtitle2">
                {t('MenusPage.publishedOnGoogle')}
            </Typography>
        ),
    },
    {
        key: 5,
        component: <Typography align="left" variant="subtitle2"></Typography>,
    },
];

const getTableBody = (menus, { companyNamesById, canManageMenu, onClickMenu, onTogglePublish, publishPendingIds, t }) =>
    menus.map(menu => [
        {
            component: (
                <Typography variant="subtitle2" align="left">
                    {menu.internalName}
                </Typography>
            ),
        },
        {
            component: renderChipsCell(menu, companyNamesById),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {menu.document?.sections?.length || 0}
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {countItems(menu)}
                </Typography>
            ),
        },
        {
            component: (
                <ToggleCell
                    checked={!!menu.publishExternally}
                    disabled={!canManageMenu(menu) || publishPendingIds.includes(menu.id)}
                    onChange={event => onTogglePublish(menu, event.target.checked)}
                />
            ),
        },
        {
            component: (
                <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {canManageMenu(menu) ? (
                        <>
                            <ActionIconButton title={t('common.edit')} onClick={() => onClickMenu(menu, 'edit')}>
                                <EditIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                            <ActionIconButton
                                title={t('MenusPage.duplicate')}
                                onClick={() => onClickMenu(menu, 'duplicate')}
                            >
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </ActionIconButton>
                            <ActionIconButton title={t('common.delete')} onClick={() => onClickMenu(menu, 'delete')}>
                                <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                            </ActionIconButton>
                        </>
                    ) : (
                        <ActionIconButton title={t('common.view')} onClick={() => onClickMenu(menu, 'view')}>
                            <VisibilityIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                    )}
                </Typography>
            ),
        },
    ]);

const EMPTY_COMPANIES = [];
const EMPTY_MENUS = [];

const MenusTable = ({ onClickMenu, page, setPage, rowsPerPage, setRowsPerPage }) => {
    const { t } = useTranslation();
    const { getWritableLocationIds } = usePermissions();
    const { data, loading } = useSelector(state => state.menus);
    const menus = data?.menus || EMPTY_MENUS;
    const companies = useSelector(state => state.account?.account?.companies) || EMPTY_COMPANIES;
    const [publishPendingIds, setPublishPendingIds] = useState([]);

    const dispatch = useDispatch();

    const onTogglePublish = (menu, enabled) => {
        setPublishPendingIds(prev => [...prev, menu.id]);
        menusService
            .setPublishExternally(menu.id, enabled)
            .then(() => dispatch(menusActions.getAll(page, rowsPerPage, { silent: true })))
            .catch(error => handleApiError(error))
            .finally(() => setPublishPendingIds(prev => prev.filter(id => id !== menu.id)));
    };

    const companyNamesById = useMemo(
        () => new Map(companies.map(company => [company.companyId, company.internalName])),
        [companies],
    );

    // Mirrors CanManageMenu: every assigned location writable; unassigned needs global (null)
    const writableLocationIds = getWritableLocationIds('microsites');
    const canManageMenu = menu => {
        if (writableLocationIds === null) return true;
        if (!menu.locationIds?.length) return false;
        return menu.locationIds.every(id => writableLocationIds.has(id));
    };

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(menusActions.getAll(newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        dispatch(menusActions.getAll(0, newRowsPerPage));
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={getTableHeader(t)}
                data={menus}
                rows={getTableBody(menus, {
                    companyNamesById,
                    canManageMenu,
                    onClickMenu,
                    onTogglePublish,
                    publishPendingIds,
                    t,
                })}
                onRowClick={menu => onClickMenu(menu, canManageMenu(menu) ? 'edit' : 'view')}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 6,
                    count: data?.totalCount || 0,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages: data?.pages || 0,
                }}
            />
        </TableContainer>
    );
};

export default MenusTable;
